import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useDebounce } from '@/shared/hooks';
import { useProjectFiles } from './useProjectFiles';
import { useProjectFolders } from './useProjectFolders';
import { useProjectFileCounts } from './useProjectFileCounts';
import { useProjectFilesActions } from './useProjectFilesActions';
import type {
  ProjectFilesViewMode,
  FolderDialogState,
  FileDialogState,
  SidebarSection,
  ProjectFolderNodeDto,
  ProjectFileDto,
  FileSortField,
  FileSortDir,
} from '../types';

// Pure tree-traversal helpers (no side effects)
function findFolderName(folders: ProjectFolderNodeDto[], id: string): string | null {
  for (const f of folders) {
    if (f.id === id) return f.name;
    if (f.children) {
      const found = findFolderName(f.children, id);
      if (found) return found;
    }
  }
  return null;
}

function getDirectFolders(
  folders: ProjectFolderNodeDto[],
  activeFolderId: string | null,
): ProjectFolderNodeDto[] {
  if (activeFolderId === null) {
    return folders.filter((f) => f.parentFolderId === null);
  }
  for (const f of folders) {
    if (f.id === activeFolderId) return f.children ?? [];
    if (f.children) {
      const found = getDirectFolders(f.children, activeFolderId);
      if (found.length > 0) return found;
    }
  }
  return [];
}

/**
 * Encapsulates all state, data fetching, computed values, effects and handlers
 * for ProjectFilesView, keeping the view component as pure JSX composition.
 */
export function useProjectFilesView(projectId: string) {
  const { t } = useTranslation('projectFiles');

  // ── Navigation ──────────────────────────────────────────────────────────────
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SidebarSection>('all');

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ProjectFilesViewMode>('list');
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [folderDialog, setFolderDialog] = useState<FolderDialogState | null>(null);
  const [fileDialog, setFileDialog] = useState<FileDialogState | null>(null);
  const [movingFolderId, setMovingFolderId] = useState<string | null>(null);
  const [movingFileIds, setMovingFileIds] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<ProjectFileDto | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<FileSortField>('name');
  const [sortDir, setSortDir] = useState<FileSortDir>('asc');
  const lastSelectedIdRef = useRef<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  // ── Server data ──────────────────────────────────────────────────────────────
  // Build query params respecting section/folderId mutual exclusion
  const filesParams = useMemo(() => {
    if (activeFolderId !== null) return { folderId: activeFolderId, sortBy, sortDir };
    if (activeSection !== 'all') return { section: activeSection, sortBy, sortDir };
    return { sortBy, sortDir };
  }, [activeFolderId, activeSection, sortBy, sortDir]);

  const { data: files = [], isLoading: filesLoading } = useProjectFiles(projectId, filesParams);
  const { data: folders = [], isLoading: foldersLoading } = useProjectFolders(projectId);
  const { data: sectionCounts } = useProjectFileCounts(projectId);

  const {
    fileMutations,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleDeleteFile,
    handleBulkDeleteFiles,
    handleMoveFile,
    handleUpload,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleMoveFolderConfirm,
    handleOpenRenameFile,
    handleRenameFileConfirm,
    handleToggleFavorite,
    handleToggleVisibility,
  } = useProjectFilesActions({
    projectId,
    activeFolderId,
    setActiveFolderId,
    folderDialog,
    setFolderDialog,
    movingFolderId,
    setMovingFolderId,
    fileDialog,
    setFileDialog,
  });

  // ── Derived values ───────────────────────────────────────────────────────────
  const visibleFiles =
    activeFolderId === null && activeSection === 'all'
      ? files.filter((f) => f.folderId === null)
      : files;
  const filteredFiles = debouncedSearch
    ? visibleFiles.filter((f) => f.fileName?.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : visibleFiles;
  const directFolders = activeSection === 'all' ? getDirectFolders(folders, activeFolderId) : [];

  // Flat ordered list of all visible IDs for shift+click range selection
  const allVisibleIds = [
    ...directFolders.map((f) => f.id).filter((id): id is string => !!id),
    ...filteredFiles.map((f) => f.id).filter((id): id is string => !!id),
  ];

  const SECTION_LABELS: Record<SidebarSection, string> = {
    all: t('sidebar.allDocs'),
    recent: t('sidebar.recent'),
    favorites: t('sidebar.favorites'),
    shared: t('sidebar.shared'),
    private: t('sidebar.private'),
  };

  const breadcrumbLabel = activeFolderId
    ? (findFolderName(folders, activeFolderId) ?? t('sidebar.allDocs'))
    : SECTION_LABELS[activeSection];

  // ── Effects ──────────────────────────────────────────────────────────────────
  // Clear selection and preview when navigating to a different folder/section
  useEffect(() => {
    startTransition(() => {
      setSelectedIds(new Set());
      setPreviewFile(null);
    });
    lastSelectedIdRef.current = null;
  }, [activeFolderId, activeSection]);

  // DnD monitor — stale-closure-safe via ref
  const handleMoveFileRef = useRef(handleMoveFile);
  useEffect(() => {
    handleMoveFileRef.current = handleMoveFile;
  });
  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const [target] = location.current.dropTargets;
        if (!target) return;
        const targetFolderId = (target.data.folderId as string) ?? null;
        if (source.data.type === 'file') {
          handleMoveFileRef.current(source.data.id as string, targetFolderId);
        }
      },
    });
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  function handleNavigateRoot() {
    setActiveFolderId(null);
    setActiveSection('all');
  }

  function handleSortDirToggle() {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }

  function handleSelect(id: string, e: MouseEvent) {
    if (e.shiftKey && lastSelectedIdRef.current) {
      const start = allVisibleIds.indexOf(lastSelectedIdRef.current);
      const end = allVisibleIds.indexOf(id);
      if (start !== -1 && end !== -1) {
        const range = allVisibleIds.slice(Math.min(start, end), Math.max(start, end) + 1);
        setSelectedIds(new Set(range));
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedIds(new Set([id]));
    }
    lastSelectedIdRef.current = id;
  }

  async function handleMoveFileConfirm(folderId: string | null) {
    await Promise.all(movingFileIds.map((id) => handleMoveFile(id, folderId)));
    setMovingFileIds([]);
  }

  return {
    // Navigation
    activeFolderId,
    setActiveFolderId,
    activeSection,
    setActiveSection,
    breadcrumbLabel,
    handleNavigateRoot,
    // UI state
    viewMode,
    setViewMode,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    handleSortDirToggle,
    sidebarOpen,
    setSidebarOpen,
    folderDialog,
    setFolderDialog,
    fileDialog,
    setFileDialog,
    movingFolderId,
    setMovingFolderId,
    movingFileIds,
    setMovingFileIds,
    previewFile,
    setPreviewFile,
    selectedIds,
    setSelectedIds,
    // Data
    folders,
    foldersLoading,
    filteredFiles,
    directFolders,
    filesLoading,
    sectionCounts,
    // Mutations / confirm
    fileMutations,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    // Action handlers
    handleDeleteFile,
    handleBulkDeleteFiles,
    handleMoveFile,
    handleUpload,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleMoveFolderConfirm,
    handleOpenRenameFile,
    handleRenameFileConfirm,
    handleToggleFavorite,
    handleToggleVisibility,
    handleSelect,
    handleMoveFileConfirm,
  };
}
